import React, { FC, useRef, useState, useEffect, ChangeEvent } from 'react';
import axios, { CancelToken, CancelTokenSource } from 'axios';
import { Segment } from '~src/interfaces/segment.interface';

import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import {
  SearchContainer,
  SearchInputContainer,
  ResultsContainer,
  NothingFoundContainer,
  InputContainer,
} from '~src/shared/sidebar/styles/search.sc';
import useSuggestionInput from '~src/services/hooks/useSuggestionInput';
import useScrollContainer from '~src/services/hooks/useScrollContainer';

import DebounceInput from './DebounceInput';
import {useTranslation} from "react-i18next";



interface Props {
  debounceTimeout: number;
  emptyMessage?: { key: string; value?: string };
  excludeSegmentIds?: string[];
  fetchSearchSuggestions: (value: string, token: CancelToken, filter?: string) => Promise<Segment[]>;
  filter?: string;
  handleClickOutside?: (event: Event) => void;
  handleSelectElement: (item: Segment, callback: () => void) => void;
  inputClassName?: string;
  inputPlaceholder?: { key: string; value?: string };
  minLength: number;
  renderListItem: (selected: boolean, value: Segment, handleSelect: (val: Segment) => void) => JSX.Element;
  setInputRef?: (input: HTMLInputElement) => void;
}

const SearchInput: FC<Props> = ({
  emptyMessage = { key: 'x.search_select.no_items' },
  excludeSegmentIds,
  fetchSearchSuggestions,
  filter,
  handleClickOutside: handleClickOutsideProperty = () => null,
  handleSelectElement,
  inputClassName = '',
  inputPlaceholder = { key: 'x.search_select.placeholder' },
  renderListItem,
  setInputRef = () => null
}) => {
  const { t } = useTranslation();
  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const inputNode = useRef<HTMLInputElement | null>(null);
  const clickOutside = useRef<((event: Event) => void) | undefined>(handleClickOutsideProperty);

  const [hitList, setHitList] = useState<Segment[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [nothingFound, setNothingFound] = useState(false);

  const { onKeyPress, selectedIndex } = useSuggestionInput(60);
  const { addEventListener, removeEventListener, boxTopPosition, checkHeight } =
    useScrollContainer(inputContainerRef);

  useEffect(() => {
    clickOutside.current = handleClickOutsideProperty;
  }, [handleClickOutsideProperty]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    addEventListener();
    checkHeight();
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      removeEventListener();
    };
  }, []);

  function handleClickOutside(event: MouseEvent) {
    const curr = inputContainerRef.current;
    if (curr && !curr.contains(event.target as Node)) {
      setNothingFound(false);
      setHitList([]);
      if (clickOutside.current) {
        clickOutside.current(event);
        clearAndHide();
      }
    }
  }

  function clearAndHide() {
    if (nothingFound || hitList.length > 0 || (inputNode && inputNode.current && inputNode.current.value)) {
      setNothingFound(false);
      setHitList([]);
      if (inputNode && inputNode.current) {
        inputNode.current.value = '';
        inputNode.current.blur();
      }
    }
  }

  function selectClickedElement(value: Segment) {
    handleSelectElement(value, clearAndHide);
  }

  function selectCurrentElement() {
    handleSelectElement(hitList[selectedIndex], clearAndHide);
  }

  function searchAction(event: ChangeEvent<HTMLInputElement>, source: CancelTokenSource): Promise<void> {
    const {
      target: { value },
    } = event;
    return new Promise((res, rej) => {
      if (value.trim().length !== 0) {
        setLoadingSuggestions(true);
        fetchSearchSuggestions(value, source.token, filter)
          .then((list) => {
            res();
            setNothingFound(list.length === 0);
            setHitList(
              list.filter((item) => !excludeSegmentIds || !excludeSegmentIds.includes(item.id)),
            );
            setLoadingSuggestions(false);
            checkHeight();
          })
          .catch((err) => {
            rej();
            if (!axios.isCancel(err)) {
              setLoadingSuggestions(false);
            }
            setHitList([]);
          });
      } else {
        res();
        setNothingFound(false);
        setHitList([]);
        setLoadingSuggestions(false);
      }
    });
  }

  function setNode(tmpInputNode: HTMLInputElement | null) {
    if (tmpInputNode) {
      inputNode.current = tmpInputNode;
      if (setInputRef) {
        setInputRef(tmpInputNode);
      }
    }
  }

  function onKeyDownItem(event: React.KeyboardEvent<HTMLDivElement>) {
    const curr = scrollContainerRef.current;
    if (curr) {
      onKeyPress(event, hitList.length, selectCurrentElement, curr);
    }
  }

  const placeholder = {
    key: inputPlaceholder ? inputPlaceholder.key : '',
    value:
      (inputPlaceholder ? inputPlaceholder.value || t(inputPlaceholder.key) : '') ||
      '',
  };

  return (
    <InputContainer ref={inputContainerRef} onKeyDown={onKeyDownItem}>
      <SearchContainer>
        <SearchInputContainer>
          <DebounceInput
            minLength={1}
            debounceTimeout={250}
            setInputNode={setNode}
            className={inputClassName}
            placeholder={placeholder}
            onChange={searchAction}
            onFocus={addEventListener}
            onBlur={removeEventListener}
          />
        </SearchInputContainer>
        {(loadingSuggestions || nothingFound || hitList.length > 0) && (
          <ResultsContainer
            style={{ maxHeight: `calc(100vh - ${boxTopPosition || '100'}px)` }}
            ref={scrollContainerRef}
          >
            {loadingSuggestions ? (
              <NothingFoundContainer>
                <LoadingSpinner size={10} />
              </NothingFoundContainer>
            ) : nothingFound ? (
              emptyMessage && (
                <NothingFoundContainer data-translation={emptyMessage.key}>
                  {emptyMessage.value || t(emptyMessage.key)}
                </NothingFoundContainer>
              )
            ) : (
              hitList.map((item, index) =>
                renderListItem(selectedIndex === index, item, selectClickedElement),
              )
            )}
          </ResultsContainer>
        )}
      </SearchContainer>
    </InputContainer>
  );
};

export default SearchInput;
